/**
 * Turn ProductGroup[] (with nested variants) into flat sellable Product rows.
 * Used by replenish, sold orders, and promotions (which still key on product/variant id).
 */
import {
  normalizeProductImages,
  primaryProductImageUrl,
} from './productImages';

export function formatVariantDisplayName(groupName, title) {
  const name = String(groupName ?? '').trim() || 'Товар';
  const variantTitle = String(title ?? '').trim();
  if (!variantTitle) return name;
  return `${name} (${variantTitle})`;
}

export function flattenProductGroups(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const out = [];

  for (const group of list) {
    const groupId = group?._id ?? group?.id;
    const groupName = group?.name ?? '';
    const images = normalizeProductImages(group?.images, group?.imageUrl ?? group?.image_url ?? null);
    const imageUrl = primaryProductImageUrl(images, group?.imageUrl ?? group?.image_url ?? null);
    const variants = Array.isArray(group?.variants) ? group.variants : [];

    // Backward-compat: already-flat row without variants
    if (variants.length === 0 && (group?.price != null || group?.quantity != null) && !group?.variants) {
      out.push({
        ...group,
        id: groupId,
        _id: groupId,
        name: groupName,
        images,
        imageUrl,
        groupId,
      });
      continue;
    }

    for (const variant of variants) {
      const variantId = variant?._id ?? variant?.id;
      if (variantId == null) continue;
      out.push({
        id: variantId,
        _id: variantId,
        groupId,
        name: formatVariantDisplayName(groupName, variant?.title),
        title: variant?.title ?? null,
        price: variant?.price,
        barcode: variant?.barcode ?? null,
        preOrder: !!variant?.preOrder,
        durationMinutes: variant?.durationMinutes ?? null,
        quantity: variant?.quantity,
        images,
        imageUrl,
        description: group?.description ?? null,
        businessId: group?.businessId,
      });
    }
  }

  return out;
}

export function rewardsFromDiscountMap(discounts) {
  return Object.entries(discounts || {})
    .map(([id, discount]) => {
      const num = discount === '' ? 0 : Number(discount);
      if (!Number.isFinite(num) || num <= 0) return null;
      const productId = Number.parseInt(String(id), 10);
      return {
        productId: Number.isFinite(productId) ? productId : id,
        discount: num,
      };
    })
    .filter(Boolean);
}

export function normalizeProductGroupsResponse(data) {
  const listRaw = Array.isArray(data)
    ? data
    : Array.isArray(data?.products)
      ? data.products
      : [];

  return listRaw.map((p) => {
    const id = p?._id ?? p?.id;
    const variants = Array.isArray(p?.variants)
      ? p.variants.map((v) => {
          const vid = v?._id ?? v?.id;
          return { ...v, _id: vid, id: vid };
        })
      : [];
    const images = normalizeProductImages(p?.images, p?.imageUrl ?? p?.image_url ?? null);
    return {
      ...p,
      _id: id,
      id,
      images,
      imageUrl: primaryProductImageUrl(images, p?.imageUrl ?? p?.image_url ?? null),
      variants,
    };
  });
}
